import { Tooltip } from '@healthinal/ui';
import React, { useRef, useState } from 'react';

type TruncateTextProps = Omit<React.ComponentPropsWithoutRef<'span'>, 'children' | 'title'> & {
  children: React.ReactNode;
  title?: React.ReactNode;
  maxWidth?: React.CSSProperties['maxWidth'];
};

const truncateStyles = {
  display: 'inline-block',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
};

function getTooltipTitle(children: React.ReactNode, title?: React.ReactNode) {
  if (title !== undefined) {
    return title;
  }

  if (typeof children === 'string' || typeof children === 'number' || typeof children === 'bigint') {
    return String(children);
  }

  return children;
}

export function TruncateText({ children, title, maxWidth, style, ...props }: TruncateTextProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const tooltipTitle = getTooltipTitle(children, title);

  const handleOpen = () => {
    const element = textRef.current;

    if (!element) {
      setOpen(false);
      return;
    }

    setOpen(element.scrollWidth > element.clientWidth);
  };

  return (
    <Tooltip
      variant="outlined"
      title={tooltipTitle}
      enterDelay={300}
      enterNextDelay={300}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={handleOpen}>
      <span
        {...props}
        ref={textRef}
        style={{
          ...truncateStyles,
          maxWidth,
          ...style,
        }}>
        {children}
      </span>
    </Tooltip>
  );
}
